import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import './styles/skeleton.css';
// Local bundled vendor styles (replaces CDN links)
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/reception-redesign.css';
import './styles/language-center.css';
// Bootstrap JS (only once; tree-shaken by Vite)
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
// NOTE: If bundle size becomes large, consider importing only needed FontAwesome icon subsets
// or migrating to an SVG icon system. For now we keep full CSS for simplicity and parity with previous CDN usage.
import App from './App.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { installApiFetchShim } from './config/api';
import { defaultQueryClientOptions } from './config/queryConfig';
import { clearPersistedQueryCache, getPersistQueryOptions } from './config/queryPersistence';
import { setQueryClient, preloadRelatedRoutes, resetRoutePreloadState } from './utils/routePreload';
import { purgePrivateCaches } from './pwa/offlineCacheClient';
import { registerAppServiceWorker } from './pwa/swRegistration';
import { ensureLogoutNavigationPurge } from './auth/logoutBoundary';

installApiFetchShim();

const rootEl = document.getElementById('root');

// Create QueryClient with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...defaultQueryClientOptions.queries,
      // Override specific settings as needed
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: defaultQueryClientOptions.mutations,
  },
});

const persistOptions = getPersistQueryOptions(queryClient);

const withQueryProviders = (children: React.ReactNode): React.ReactNode => {
  if (persistOptions) {
    return (
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        {children}
      </PersistQueryClientProvider>
    );
  }

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

// Set up query client for route preloading with data prefetching
setQueryClient(queryClient);

// Preload related routes after initial render
if (typeof window !== 'undefined') {
  // Use requestIdleCallback to preload related routes after app is interactive
  const schedulePreload = typeof requestIdleCallback === 'function'
    ? requestIdleCallback
    : (fn: () => void) => setTimeout(fn, 2000);
  
  schedulePreload(() => {
    preloadRelatedRoutes(window.location.pathname);
  });

  void registerAppServiceWorker();

  const logoutHandler = (event: Event) => {
    ensureLogoutNavigationPurge(event);
    queryClient.clear();
    void clearPersistedQueryCache();
    resetRoutePreloadState();
    void purgePrivateCaches();
  };
  window.addEventListener('app:logout', logoutHandler);
}

if (rootEl) {
  const root = createRoot(rootEl);
  root.render(
    <StrictMode>
      <HelmetProvider>
        {withQueryProviders(<App />)}
      </HelmetProvider>
    </StrictMode>
  );
}
